/**
 * CustomerPlannedPage — a planned, not yet built, customer tab,
 * `/customers/:customerId/planned/:section` (docs/features/order-to-invoice/
 * 16_frontend_followup_spec.md §3.2): the customer's frame around the one
 * "not built yet" line. The route belongs to the `previews` feature, so in
 * production it is not found; a key the manifest does not plan is not found
 * either.
 *
 * The `NotBuiltYet` ids are literals because `scripts/check-planned-sections.mjs`
 * reads them; building a tab deletes its manifest entry and its line here.
 */
import { Link, useParams } from "react-router";
import {
  plannedSectionsFor,
  type PlannedTabKey,
} from "@shared-core/domain/planned-sections";
import { paths } from "@shared-core/routing/routes";
import { Card } from "../app/components/primitives/card";
import { CustomerFrame } from "../app/components/domain/customers/CustomerFrame";
import { NotBuiltYet } from "../app/components/patterns/NotBuiltYet";
import { useCustomerRouteId } from "../app/components/domain/customers/useCustomerRouteId";

function PlannedCustomerTab({ sectionKey }: { sectionKey: string }) {
  switch (sectionKey) {
    case "stock":
      return <NotBuiltYet planned="customer.tab.stock" />;
    default:
      return null;
  }
}

export function CustomerPlannedPage() {
  const customerId = useCustomerRouteId();
  const { section = "" } = useParams<{ section: string }>();
  const isPlanned = plannedSectionsFor("customer", "tab").some(
    (s) => s.key === section,
  );
  if (customerId === null) return null;
  if (!isPlanned) {
    return (
      <Card className="p-12 text-center">
        <h1 className="mb-2 text-lg">Page not found</h1>
        <p className="text-muted-foreground">
          This customer has no “{section}” tab.{" "}
          <Link to={paths.customerDetail({ customerId })} className="underline">
            Open the customer
          </Link>
          .
        </p>
      </Card>
    );
  }
  return (
    <CustomerFrame
      customerId={customerId}
      current={section as PlannedTabKey<"customer">}
    >
      <PlannedCustomerTab sectionKey={section} />
    </CustomerFrame>
  );
}
